import apiClient from '../lib/api-client';

export interface DashboardStats {
  schools: {
    total: number;
    active: number;
    inactive: number;
    pending: number;
  };
  classes: {
    total: number;
    active: number;
    inactive: number;
    archived: number;
  };
  teachers: {
    total: number;
    active: number;
    inactive: number;
  };
  students: {
    total: number;
    active: number;
    inactive: number;
  };
  recentActivities: Activity[];
  schoolDistribution: SchoolDistribution[];
  classDistribution: ClassDistribution[];
  monthlyStats: MonthlyStats[];
}

// Book-related dashboard interfaces
export interface BookDashboardStats {
  totalBooks: number;
  activeSchools: number;
  totalStudents: number;
  activationCodesUsed: number;
  trends?: {
    books: number;
    schools: number;
    students: number;
    activationCodes: number;
  };
}

export interface WeeklyChartData {
  name: string;
  booksDistributed: number;
  booksRead: number;
  newRegistrations: number;
}

export interface BookCategory {
  name: string;
  value: number;
  color: string;
}

export interface BookActivity {
  id: string | number;
  type: string;
  user: string;
  book: string;
  time: string;
  icon: string;
  color: string;
  students: number;
}

export interface SchoolPerformance {
  name: string;
  students: number;
  booksAssigned: number;
  completion: number;
  activeUsers: number;
}

export interface ActivationCodeStats {
  name: string;
  codesActivated: number;
  codesCreated: number;
  date: string;
}

export interface UserAssetStats {
  name: string;
  assetsCreated: number;
  assetsDownloaded: number;
  uniqueDownloaders: number;
  date: string;
}

export interface Activity {
  id: string;
  type: 'school' | 'class' | 'teacher' | 'student';
  action: 'created' | 'updated' | 'deleted' | 'activated' | 'deactivated';
  description: string;
  timestamp: string;
  user: string;
}

export interface SchoolDistribution {
  type: string;
  count: number;
  percentage: number;
}

export interface ClassDistribution {
  grade: string;
  count: number;
  percentage: number;
}

export interface MonthlyStats {
  month: string;
  schools: number;
  classes: number;
  teachers: number;
  students: number;
}

class DashboardService {
  private readonly baseUrl = '/dashboard';

  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Fetch all admin dashboard data in parallel
      const [statsRes, activitiesRes, schoolDistRes, classDistRes, monthlyRes] = await Promise.all([
        apiClient.get(`${this.baseUrl}/admin/stats`),
        apiClient.get(`${this.baseUrl}/admin/recent-activities?limit=10`),
        apiClient.get(`${this.baseUrl}/admin/school-distribution`),
        apiClient.get(`${this.baseUrl}/admin/class-distribution`),
        apiClient.get(`${this.baseUrl}/admin/monthly-stats`)
      ]);

      return {
        ...statsRes.data.data,
        recentActivities: activitiesRes.data.data,
        schoolDistribution: schoolDistRes.data.data,
        classDistribution: classDistRes.data.data,
        monthlyStats: monthlyRes.data.data
      };
    } catch (error) {
      console.error('Failed to fetch admin dashboard stats:', error);
      throw error;
    }
  }

  async getRecentActivities(limit: number = 10): Promise<Activity[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/admin/recent-activities?limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch recent activities:', error);
      throw error;
    }
  }

  async getSchoolDistribution(): Promise<SchoolDistribution[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/admin/school-distribution`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch school distribution:', error);
      throw error;
    }
  }

  async getClassDistribution(): Promise<ClassDistribution[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/admin/class-distribution`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch class distribution:', error);
      throw error;
    }
  }

  async getMonthlyStats(): Promise<MonthlyStats[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/admin/monthly-stats`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch monthly stats:', error);
      throw error;
    }
  }

  // Mock data methods for development/fallback
  private getMockStats(): DashboardStats {
    return {
      schools: {
        total: 24,
        active: 20,
        inactive: 3,
        pending: 1
      },
      classes: {
        total: 156,
        active: 142,
        inactive: 8,
        archived: 6
      },
      teachers: {
        total: 324,
        active: 298,
        inactive: 26
      },
      students: {
        total: 2847,
        active: 2654,
        inactive: 193
      },
      recentActivities: this.getMockActivities(),
      schoolDistribution: this.getMockSchoolDistribution(),
      classDistribution: this.getMockClassDistribution(),
      monthlyStats: this.getMockMonthlyStats()
    };
  }

  private getMockActivities(): Activity[] {
    return [
      {
        id: '1',
        type: 'school',
        action: 'created',
        description: 'Trường THPT Nguyễn Du đã được tạo mới',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        user: 'Admin User'
      },
      {
        id: '2',
        type: 'class',
        action: 'created',
        description: 'Lớp 10A1 đã được tạo mới tại Trường THPT Nguyễn Du',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        user: 'Admin User'
      },
      {
        id: '3',
        type: 'teacher',
        action: 'created',
        description: 'Giáo viên Nguyễn Văn A đã được thêm vào hệ thống',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
        user: 'Admin User'
      },
      {
        id: '4',
        type: 'student',
        action: 'created',
        description: '15 học sinh mới đã được đăng ký vào lớp 10A1',
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
        user: 'Admin User'
      },
      {
        id: '5',
        type: 'school',
        action: 'activated',
        description: 'Trường THCS Lê Lợi đã được kích hoạt',
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        user: 'Admin User'
      }
    ];
  }

  private getMockSchoolDistribution(): SchoolDistribution[] {
    return [
      { type: 'Trường Công lập', count: 15, percentage: 62.5 },
      { type: 'Trường Tư thục', count: 6, percentage: 25.0 },
      { type: 'Trường Quốc tế', count: 2, percentage: 8.3 },
      { type: 'Trường Nghề', count: 1, percentage: 4.2 }
    ];
  }

  private getMockClassDistribution(): ClassDistribution[] {
    return [
      { grade: 'Lớp 10', count: 52, percentage: 33.3 },
      { grade: 'Lớp 11', count: 48, percentage: 30.8 },
      { grade: 'Lớp 12', count: 45, percentage: 28.8 },
      { grade: 'Lớp 9', count: 11, percentage: 7.1 }
    ];
  }

  private getMockMonthlyStats(): MonthlyStats[] {
    const months = ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6'];
    return months.map((month, index) => ({
      month,
      schools: Math.floor(Math.random() * 5) + 1,
      classes: Math.floor(Math.random() * 20) + 10,
      teachers: Math.floor(Math.random() * 30) + 20,
      students: Math.floor(Math.random() * 200) + 100
    }));
  }

  // Book-related dashboard methods
  async getBookDashboardStats(timeRange: string = '1month'): Promise<BookDashboardStats> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/stats?timeRange=${timeRange}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch book dashboard stats:', error);
      throw error;
    }
  }

  async getWeeklyChartData(): Promise<WeeklyChartData[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/weekly-chart`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch weekly chart data:', error);
      throw error;
    }
  }

  async getBookCategories(): Promise<BookCategory[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/book-categories`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch book categories:', error);
      throw error;
    }
  }

  async getBookRecentActivities(limit: number = 10): Promise<BookActivity[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/recent-activities?limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch book recent activities:', error);
      throw error;
    }
  }

  async getSchoolPerformance(limit: number = 5): Promise<SchoolPerformance[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/school-performance?limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch school performance:', error);
      throw error;
    }
  }

  async getActivationCodeStats(timeRange: string = '1month'): Promise<ActivationCodeStats[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/activation-code-stats?timeRange=${timeRange}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch activation code statistics:', error);
      throw error;
    }
  }

  async getUserAssetStats(timeRange: string = '1month'): Promise<UserAssetStats[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/user-asset-stats?timeRange=${timeRange}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch user asset statistics:', error);
      throw error;
    }
  }
}

export default new DashboardService();
